import {
  AssignPhoneCallForm,
  AssignPhoneCallSchema,
  CallDto,
} from '@act/shared/data-transfer-objects';
import {
  CardActions,
  CardContent,
  Dialog,
  Divider,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { callsRequests } from 'redux/requests/calls.request';
import { SelectOption } from 'react-components/components/search-bar';
import { useApiCall } from 'redux/utils/api-hook';
import { GenericDialogProps } from 'utilities/dialog-context/dialog-context-helpers';
import {
  DialogContentContainer,
  DialogTitleBarComponent,
} from '../dialog-forms/components/SharedDialogHookComponents';
import { membersRequests } from 'redux/requests/members.request';
import { Button, FormBuilder } from '@act/storybook';
import { useToast } from '../app/ReusableToast';
import useUnsavedChangesDialog from '../dialog-forms/hooks/use-unsaved-changes-dialog';
import { updateSubjectIdSuccess } from 'redux/slices/callsSlice';
import { getMemberInfo } from 'redux/slices/memberSlice';
import { useAppDispatch } from 'redux/store-hooks';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { TrackingProvider } from 'react-components/tracking';

export interface AssignPhoneCallUniqueProps {
  call: CallDto;
}

export type AssignPhoneCallDialogProps = GenericDialogProps<boolean> &
  AssignPhoneCallUniqueProps;

export type AssignPhoneCallDialogCallbackInput = {};

const AssignPhoneCallDialog = ({
  call,
  open,
  setOpen,
  callback,
}: AssignPhoneCallDialogProps) => {
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const dispatch = useAppDispatch();

  const [typeaheadUsers, isLoadingTypeahead, getTypeaheadUsers] = useApiCall(
    membersRequests.getPaginatedMembersForTable
  );
  const [updatedCall, isUpdatingCall, updateSubjectId] = useApiCall(
    callsRequests.updateSubjectId
  );

  const [typeaheadOptions, setTypeaheadOptions] = useState<SelectOption[]>([]);

  const { control, handleSubmit, formState, setValue } =
    useForm<AssignPhoneCallForm>({
      mode: 'onChange',
      resolver: zodResolver(AssignPhoneCallSchema),
      defaultValues: {
        interactableId: '',
      },
    });

  const isLoading = isUpdatingCall;
  const isSubmitting = isLoading || formState.isSubmitting;

  useEffect(() => {
    if (typeaheadUsers?.users) {
      const options = typeaheadUsers.users.map(u => ({
        label: `${u.firstName} ${u.lastName}`,
        value: u.id,
      }));
      setTypeaheadOptions(options);
    } else {
      setTypeaheadOptions([]);
    }
  }, [typeaheadUsers]);

  useEffect(() => {
    if (open) {
      setTypeaheadSearchTerm('');
    }
  }, [open]);

  async function setTypeaheadSearchTerm(searchTerm: string) {
    const overrideFilters = {
      searchTerm,
      'page-start': 0,
      'page-size': 10,
      includeCount: true,
      orderBy: null,
      orderDirection: null,
      includeDetails: true,
    };
    const filterConfig = {
      ...overrideFilters,
    };
    void getTypeaheadUsers({ params: filterConfig });
  }

  const formConfig: FormConfig<AssignPhoneCallForm> = [
    {
      type: 'input',
      input: 'searchBar',
      name: 'interactableId',
      label: 'Search by member name',
      props: {
        options: typeaheadOptions,
        isLoading: isLoadingTypeahead,
        onTypeaheadSearch: setTypeaheadSearchTerm,
        trackingName: 'member-search-input',
        onSearch: (searchTerm: string | SelectOption | null) => {
          const value =
            typeof searchTerm === 'string'
              ? searchTerm
              : searchTerm?.value || '';
          setValue('interactableId', value, {
            shouldDirty: true,
            shouldValidate: true,
          });
        },
      },
    },
  ];

  useEffect(() => {
    if (updatedCall && !isUpdatingCall) {
      dispatch(updateSubjectIdSuccess(updatedCall));
      if (updatedCall.subjectId) {
        dispatch(
          getMemberInfo({ params: { memberId: updatedCall.subjectId } })
        );
      }
      toast('Call assigned successfully!', 'success');
      callback?.(true);
      setOpen(false);
    }
  }, [updatedCall, isUpdatingCall, callback]);

  const onSubmit = async (data: AssignPhoneCallForm) => {
    try {
      updateSubjectId({
        params: { id: call?.id },
        body: { subjectId: data.interactableId },
      });
    } catch (error) {
      toast('An error occurred. Please try again.', 'error');
    }
  };

  const { isDirty } = formState;
  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
        callback?.(false);
      });
    } else {
      setOpen(false);
      callback?.(false);
    }
  };

  return (
    <TrackingProvider contextName="assign-phone-call-dialog">
      <div style={{ width: '600px' }}>
        <Dialog open={open} onClose={handleClose} fullWidth>
        <DialogContentContainer>
          <DialogTitleBarComponent
            title="Assign call"
            handleClose={handleClose}
          />
          <form onSubmit={handleSubmit(onSubmit)}>
            <CardContent sx={{ padding: '12px 24px 0' }}>
              <Typography variant="titleMedium" sx={{ marginBottom: '16px' }}>
                Select a member for this call
              </Typography>
              <FormBuilder
                config={formConfig}
                control={control}
                formState={formState}
              />
            </CardContent>
            <Divider sx={{ marginTop: '48px' }} />
            <CardActions
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'right',
                padding: '8px 24px',
              }}
            >
              <Button
                onClick={handleClose}
                disabled={isSubmitting}
                trackingName="cancel-button"
              >
                Cancel
              </Button>
              <Button
                disabled={!formState.isValid || isSubmitting}
                debounced
                onClick={handleSubmit(onSubmit)}
                type="submit"
                variant="contained"
                trackingName="assign-call-button"
              >
                {isSubmitting ? 'Assigning...' : 'Assign call'}
              </Button>
            </CardActions>
          </form>
        </DialogContentContainer>
      </Dialog>
    </div>
    </TrackingProvider>
  );
};

export default AssignPhoneCallDialog;
